/**
 * useIdleTimeout — React wiring for the idle-activity detector.
 *
 * Mounts a `createIdleDetector` while a user session is active. On
 * timeout:
 *
 *   1. Best-effort POST to the `auth-idle` Edge Function so the
 *      `auth.idle_timeout` audit row is written under the user's JWT.
 *      Fire-and-forget — sign-out proceeds even if the emit fails
 *      (e.g. because the JWT just expired). The Edge Function logs
 *      its own AUDIT_FAILED outcome on the server side.
 *   2. `signOut()` clears the Supabase session.
 *   3. `RequireAuth` then redirects to `/login` on the next render —
 *      no explicit navigation needed here.
 */

import { useEffect, useRef } from 'react'
import { supabase }                       from '@/lib/supabase'
import { useSignOut }                     from '@/hooks/useAuth'
import { useAuthStore }                   from '@/store/authStore'
import { createIdleDetector, IDLE_TIMEOUT_MS } from '@/lib/auth/idleTimeout'

export function useIdleTimeout(timeoutMs: number = IDLE_TIMEOUT_MS): void {
  const userId  = useAuthStore((s) => s.user?.id ?? null)
  const signOut = useSignOut()

  // Stable refs so the detector effect can resubscribe only when
  // userId / timeoutMs change — not on every render of the parent.
  // The write lives in an effect, not the render body: refs must not be
  // mutated during render. `onTimeout` only ever reads `.current` from a
  // timer callback, which runs long after the commit that updated it.
  const signOutRef = useRef(signOut)
  useEffect(() => {
    signOutRef.current = signOut
  }, [signOut])

  useEffect(() => {
    if (!userId) return

    let cancelled = false
    const detector = createIdleDetector({
      timeoutMs,
      onTimeout: () => {
        if (cancelled) return
        // Server-side audit is fire-and-forget. We do not await: a slow
        // network must not delay the sign-out the user just earned. The
        // emit is best-effort; the Edge Function returns AUDIT_FAILED if
        // the underlying audit row write fails, which surfaces in the
        // Edge Function's runtime logs.
        void supabase.functions.invoke('auth-idle', { body: {} })
        void signOutRef.current()
      },
    })
    detector.start()

    return () => {
      cancelled = true
      detector.stop()
    }
  }, [userId, timeoutMs])
}
